import * as vp from 'vopay';

import { FunctionComponent, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubAccountSearchForm from './SubAccountSearchForm';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';

const { formatAsCurrency, CurrencySymbols, Badge, Box, Button, Table } = vp;

type SubAccountsListProps = {
  subAccountsList: vp.AccountBalance[];
  details: vp.AccountDetailsMap;
  exportCSV: (accountId: string | undefined) => void;
};

type ExpandedAccountBalance = vp.AccountBalance & {
  IsExpanded: boolean;
};

type FC = FunctionComponent<SubAccountsListProps>;

const SubAccountsList: FC = (props: SubAccountsListProps) => {
  const { subAccountsList, details, exportCSV } = props;
  const [updatedList, setUpdatedList] = useState<ExpandedAccountBalance[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [countries, setCountries] = useState<string[]>([]);
  const [shouldGetCountryList, setShouldGetCountryList] = useState(true);
  const { canCreateAccount } = useModuleFeatureFlag('accounts');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLists, setFilteredLists] = useState<ExpandedAccountBalance[]>([]);
  const [country, setCountry] = useState('');

  useEffect(() => {
    if (subAccountsList.length > 0) {
      setUpdatedList(subAccountsList.map((item) => ({ ...item, IsExpanded: true })));
    }

    if (subAccountsList.length > 0 && shouldGetCountryList) {
      const uniqueCurrencies = [...new Set(subAccountsList.map((item) => item.Country))].sort((a, b) =>
        a === 'Canada' ? -1 : String(a).localeCompare(String(b)),
      );

      setShouldGetCountryList(false);
      setCountries(uniqueCurrencies);
    }
  }, [subAccountsList]);

  useEffect(() => {
    const keys = ['AccountName', 'DBAName'];
    setFilteredLists(
      updatedList.filter(
        (data: ExpandedAccountBalance) =>
          (country === '' || country === 'All' || data.Country === country) &&
          (searchTerm === '' ||
            keys.some((key) =>
              data[key as keyof ExpandedAccountBalance]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
            )),
      ),
    );
  }, [searchTerm, updatedList, country]);

  const sortFunction = (sortDirectionFunc: 'asc' | 'desc' | null, sortColumnFunc: string | null) => {
    setSortColumn(sortColumnFunc);
    setSortDirection(sortDirectionFunc);
  };

  const tableProps: vp.TableProps = {
    name: 'sub-account-list',
    columns: {
      accountName: { header: 'Account Name', align: 'left' },
      status: { header: 'Status', align: 'left' },
      country: { header: 'Country', align: 'left' },
      pending: { header: 'Pending Balance', align: 'left' },
      available: { header: 'Available Balance', align: 'left' },
      view: { header: '', isSortable: false, align: 'right' },
    },
    rows:
      filteredLists
        .map((balance) => ({
          accountName: (
            <>
              {balance.AccountName}
              <br />
              <i>{balance.DBAName || ''}</i>
            </>
          ),
          status: (
            <Box jc="fs">
              {balance.IsActive ? (
                <Badge label={vp.AccountStatus.Active} color="positive" hasIcon width="85px" />
              ) : (
                <Badge label={balance.AccountStatus} color={vp.GetColorStatus(balance?.AccountStatus || '')} hasIcon />
              )}
            </Box>
          ),
          country: balance.Country,
          pending: `${balance.Currency} ${formatAsCurrency(balance.PendingBalance, CurrencySymbols[balance.Currency])}`,
          available: `${balance.Currency} ${formatAsCurrency(
            balance.AvailableBalance,
            CurrencySymbols[balance.Currency],
          )}`,
          view: (
            <Link
              style={{ textDecoration: 'none', color: '#2F82FF' }}
              to={{
                pathname: `sub-account/${balance.AccountID}`,
                search: new URLSearchParams({
                  accountId: balance.AccountID,
                  isActive: balance.IsActive.toString(),
                  accountStatus: balance.AccountStatus,
                  currency: balance.Currency,
                  pending: balance.PendingBalance.toString(),
                  available: balance.AvailableBalance.toString(),
                  account: balance.AccountBalance.toString(),
                  reserve: balance.Reserve.toString(),
                  immediate: balance.AvailableImmediately.toString(),
                }).toString(),
              }}
            >
              <Box jc="fe">
                <Button
                  id="view-account"
                  size="sm"
                  color="lined"
                  label="View"
                  bold={false}
                  width="130px"
                  textColor="#2D2D2D"
                  fontWeight={500}
                  textDecoration="none"
                />
              </Box>
            </Link>
          ),
        }))
        .sort((a, b) => {
          if (!sortDirection || !sortColumn || !Object.prototype.hasOwnProperty.call(a, sortColumn)) {
            return 0;
          }

          const aValue = a[sortColumn as keyof typeof a];
          const bValue = b[sortColumn as keyof typeof b];

          // Adjust the comparison based on the data type of the column
          if (sortColumn === 'accountName') {
            return sortDirection === 'asc'
              ? vp.extractTextFromJSX(aValue).localeCompare(vp.extractTextFromJSX(bValue))
              : vp.extractTextFromJSX(bValue).toString().localeCompare(vp.extractTextFromJSX(aValue));
          }

          if (sortColumn === 'status') {
            return sortDirection === 'asc'
              ? vp.extractTextFromBadgeJSX(aValue).localeCompare(vp.extractTextFromBadgeJSX(bValue))
              : vp.extractTextFromBadgeJSX(bValue).toString().localeCompare(vp.extractTextFromBadgeJSX(aValue));
          }

          if (sortColumn === 'pending' || sortColumn === 'available') {
            const extractNumber = (balance: string) => parseFloat(balance.replace(/[^0-9.-]+/g, ''));
            const extractedTextFromJSXaValue = extractNumber(vp.extractTextFromJSX(a.available));
            const extractedTextFromJSXbValue = extractNumber(vp.extractTextFromJSX(b.available));

            return sortDirection === 'asc'
              ? extractedTextFromJSXaValue - extractedTextFromJSXbValue
              : extractedTextFromJSXbValue - extractedTextFromJSXaValue;
          }
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
          return sortDirection === 'asc' ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
        }) || [],
  };

  return (
    <div>
      <SubAccountSearchForm
        countries={countries}
        canCreateAccount={canCreateAccount}
        searchTerm={searchTerm}
        handleSearchTermChange={setSearchTerm}
        country={country}
        handleExportCSV={exportCSV}
        handleCountryChange={setCountry}
        details={details}
      />
      <Table {...tableProps} sortable sortFunction={sortFunction} />
    </div>
  );
};

export default SubAccountsList;
